import type { ErpModule } from '@/api/platform.api';
import { enabledHeadModuleEntries } from '@/config/moduleEntries';
import type { ScreenPermissionRow } from '@/utils/screenPermissionMatrix';

export const MODULE_ACCESS_PATH_PREFIX = '__module-access__/';

/** Primary permission codes that open a head-module application (mirrors backend). */
export const MODULE_APPLICATION_VIEW_CODES: Partial<Record<ErpModule, string[]>> = {
  HR: ['HR_VIEW', 'HR_ESS_ACCESS'],
  CRM: ['CRM_VIEW'],
  POS: ['BILLING_CREATE', 'BILLING_VIEW'],
  FINANCE: ['FINANCE_VIEW'],
  PROJECTS: ['PROJECTS_VIEW'],
  DESK: ['DESK_VIEW'],
  SOCIAL: ['SOCIAL_VIEW'],
  PROCUREMENT: ['PROCUREMENT_VIEW'],
  WAREHOUSE: ['WAREHOUSE_VIEW'],
  LEAD_GEN: ['LEAD_GEN_VIEW'],
  MARKETING: ['MARKETING_VIEW'],
};

export function moduleApplicationAccessPath(erpModule: ErpModule): string {
  return `${MODULE_ACCESS_PATH_PREFIX}${erpModule}`;
}

export function isModuleApplicationAccessRow(path: string): boolean {
  return path.startsWith(MODULE_ACCESS_PATH_PREFIX);
}

export function moduleApplicationViewCodes(erpModule: ErpModule): string[] {
  return MODULE_APPLICATION_VIEW_CODES[erpModule] ?? [`${erpModule}_VIEW`];
}

/** One view toggle per org-subscribed head module — controls app launcher membership. */
export function buildModuleApplicationRows(
  enabledModules: ErpModule[],
  codeToId: Map<string, number>,
): ScreenPermissionRow[] {
  const rows: ScreenPermissionRow[] = [];
  for (const entry of enabledHeadModuleEntries(enabledModules)) {
    const codes = moduleApplicationViewCodes(entry.erpModule);
    const viewIds = [...new Set(codes.map((code) => codeToId.get(code)).filter((id): id is number => id != null))];
    if (viewIds.length === 0) continue;
    rows.push({
      path: moduleApplicationAccessPath(entry.erpModule),
      label: entry.label,
      moduleKey: 'APPLICATIONS',
      moduleLabel: 'Applications',
      groupLabel: 'Login access',
      rule: { view: codes },
      permissionIds: {
        view: viewIds,
        create: [],
        edit: [],
        delete: [],
      },
      viewGrantIds: viewIds,
      openAccess: false,
    });
  }
  return rows.sort((a, b) => a.label.localeCompare(b.label));
}
